import React, { useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import { Box,Grid2, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField, Typography, IconButton, InputAdornment, MenuItem } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../../components/alertProvider';
import { initialNewPatientValues, NewPatientCreatePayload, newPatientValidationSchema, useNewPatientMasters, registerPatientURL, useRegisterPatient, ExistingPatientsResponse, getExistingPatient, } from './NewPatientPopUp.helper';
import { getRequest } from '../../../services/httpService';
import { useAppQuery } from '../../../services/appQuery';
import { RequiredLabel } from '../../../utils/commonUtils';

type Mode = 'new' | 'existing';

interface NewPatientPopUpProps {
  open: boolean;
  onClose: () => void;
}

const fallbackCountryCodeOptions = [{ label: '+91', value: '91' }];

const NewPatientPopUp: React.FC<NewPatientPopUpProps> = ({ open, onClose }) => {
  const [mode, setMode] = useState<Mode>('new');
  const [selectedPatient, setSelectedPatient] = useState<{ label: string; value: number } | null>(null);
  const { genders, countryCodes, referralDoctors } = useNewPatientMasters();
  const { addAlert } = useAlert();
  const registerPatientMutation = useRegisterPatient();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const { data: existingPatients, refetch: refetchPatients, isFetching } = useAppQuery<ExistingPatientsResponse>(
    ['getExistingPatient', searchValue],
    () => getRequest<ExistingPatientsResponse>(getExistingPatient(7, searchValue)),
    { staleTime: 0, debounceMs: 300, enabled: !!searchValue, refetchOnMount: true }
  );

  const genderOptions = useMemo(
    () =>
      genders && genders.length
        ? genders.map((g) => ({ label: g.name_label, value: String(g.id) }))
        : [],
    [genders]
  );

  const countryCodeOptions = useMemo(
    () =>
      countryCodes && countryCodes.length
        ? countryCodes.map((c) => ({ label: `+${c.dial_code} ${c.name}`, value:`+${c.dial_code}` }))
        : fallbackCountryCodeOptions,
    [countryCodes]
  );

  const referralDoctorOptions = useMemo(
    () =>
      referralDoctors && referralDoctors.length
        ? referralDoctors.map((d) => {
          const fullName = [d.first_name, d.last_name]
            .filter(Boolean)
            .join(' ')
            .trim();
          const label = fullName || d.username || d.email;
          return { id: d.id, label };
        })
        : [],
    [referralDoctors]
  );

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (values: NewPatientCreatePayload) => {
    registerPatientMutation.mutate(
      {
        path: registerPatientURL(),
        body: values,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSuccess: (data: any) => {
          const id = data?.data?.id ?? data?.data?.patient_id;
          if (id) {
            navigate(`new-pharmacy-details/${id}`);
          }
          addAlert(data?.message ?? 'Patient registered successfully', 'success');
          onClose();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          addAlert(error?.message || 'Failed to register patient', 'error');
        },
      }
    );
  };

  
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" slotProps={{ paper: { sx: { borderRadius: 4, maxWidth: 960, width: '100%', }, }, }} >
      <DialogTitle sx={{ px: 4, pt: 3, pb: 2, borderBottom: '1px solid #E5E7EB', }} >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography component="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 22, }} >
            New Invoice
          </Typography>

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              backgroundColor: '#F3F4F6',
              '&:hover': { backgroundColor: '#E5E7EB' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <Formik<NewPatientCreatePayload>
        initialValues={initialNewPatientValues}
        validationSchema={newPatientValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleBlur, setFieldValue, touched, errors }) => (
          <Form>
            <DialogContent sx={{ px: 4, pt: 3, pb: 1 }}>
              {/* Mode toggle */}
              <RadioGroup
                row
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
                sx={{ mb: 3, columnGap: 4 }}
              >
                <FormControlLabel
                  value="new"
                  control={(
                    <Radio
                      sx={{
                        color: '#E53935',
                        '&.Mui-checked': { color: '#E53935' },
                      }}
                    />
                  )}
                  label={
                    <Typography sx={{ fontWeight: mode === 'new' ? 600 : 500 }}>
                      Add New Patient
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="existing"
                  control={(
                    <Radio
                      sx={{
                        color: '#E53935',
                        '&.Mui-checked': { color: '#E53935' },
                      }}
                    />
                  )}
                  label={
                    <Typography sx={{ fontWeight: mode === 'existing' ? 600 : 500 }}>
                      Add Existing Patient
                    </Typography>
                  }
                />
              </RadioGroup>

              {mode === 'new' ? (
                <Grid2 container direction="column" rowSpacing={2.5}>
                  <Grid2 container columnSpacing={2} rowSpacing={2}>
                    <Grid2 size={{xs:12,md:6}}>
                      <TextField
                        fullWidth
                        name="first_name"
                        label={RequiredLabel('First Name', true)}
                        placeholder="Enter First Name"
                        size="small"
                        variant="outlined"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.first_name && errors.first_name)}
                      />
                    </Grid2>
                    <Grid2 size={{xs:12,md:6}}>
                      <TextField
                        fullWidth
                        name="last_name"
                        label={RequiredLabel('Last Name', true)}
                        placeholder="Enter Last Name"
                        size="small"
                        variant="outlined"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.last_name && errors.last_name)}
                      />
                    </Grid2>
                  </Grid2>

                  <Grid2 container columnSpacing={2} rowSpacing={2}>
                    <Grid2 size={{xs:12,md:6}}>
                      <TextField
                        fullWidth
                        name="date_of_birth"
                        label={RequiredLabel('Date of Birth', true)}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        size="small"
                        type="date"
                        variant="outlined"
                        value={values.date_of_birth}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        inputProps={{
                          max: new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0],
                        }}
                        error={Boolean(touched.date_of_birth && errors.date_of_birth)}
                      />
                    </Grid2>
                    <Grid2 size={{xs:12,md:6}}>
                      <TextField
                        select
                        name="gender"
                        label={RequiredLabel('Gender', true)}
                        fullWidth
                        placeholder="Select Gender"
                        size="small"
                        variant="outlined"
                        value={values.gender}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.gender && errors.gender)}
                      >
                        {genderOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid2>
                  </Grid2>

                  <Grid2 container columnSpacing={2} rowSpacing={2}>
                    <Grid2 size={{ xs: 12, md: 2 }}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={countryCodeOptions}
                        getOptionLabel={(opt) => opt.label}
                        value={
                          countryCodeOptions.find(
                            (opt) => opt.value === values.mobile_number_country_code
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          setFieldValue('mobile_number_country_code', newValue?.value || '');
                        }}
                        ListboxProps={{
                          style: {
                            maxHeight: 400,
                            overflow: 'auto',
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            name="mobile_number_country_code"
                            label={RequiredLabel('Country Code', true)}
                            placeholder="Country Code"
                            size="small"
                            variant="outlined"
                            onBlur={handleBlur}
                            error={Boolean(touched.mobile_number_country_code && errors.mobile_number_country_code)}

                          />
                        )}
                      />
                    </Grid2>
                    <Grid2 size={{xs:12,md:4}}>
                      <TextField
                        fullWidth
                        name="mobile_number"
                        label={RequiredLabel('Mobile Number', true)}
                        placeholder="Enter Mobile Number"
                        size="small"
                        variant="outlined"
                        value={values.mobile_number}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.mobile_number && errors.mobile_number)}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Autocomplete
                        freeSolo
                        options={referralDoctorOptions}
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? option : option.label
                        }
                        value={values.referral_doctor_name}
                        onChange={(_, newValue, reason) => {
                          if (reason === 'selectOption' && newValue && typeof newValue !== 'string') {
                            const opt = newValue as { id: number; label: string };
                            setFieldValue('referral_doctor_id', opt.id);
                            setFieldValue('referral_doctor_name', opt.label);
                          } else {
                            setFieldValue('referral_doctor_id', null);
                            setFieldValue(
                              'referral_doctor_name',
                              (typeof newValue === 'string' ? newValue : '') || ''
                            );
                          }
                        }}
                        onInputChange={(_, newInputValue, reason) => {
                          if (reason === 'input') {
                            setFieldValue('referral_doctor_id', null);
                            setFieldValue('referral_doctor_name', newInputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            name="referral_doctor_name"
                            label="Referred Doctor"
                            placeholder="Referred Doctor"
                            size="small"
                            variant="outlined"
                            onBlur={handleBlur}
                          />
                        )}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ mb: 0.5, fontSize: 14 }}>Search Patient</Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={existingPatients?.data?.objects?.map((p) => ({ label: `${p.patient_name} (${p.medical_record_no})`, value: p.patient_id })) || []}
                    getOptionLabel={(opt) => opt.label}
                    loading={isFetching}
                    value={selectedPatient}
                    onChange={(_, newValue) => {
                      setSelectedPatient(newValue);
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') {
                        setSearchValue(value);
                        if (value.length > 1) {
                          refetchPatients();
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search Patient"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                px: 4,
                pb: 3,
                pt: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outlined"
                  color="inherit"
                  sx={{
                    flex: 1,
                    borderRadius: 3,
                    fontSize: 16,
                    textTransform: 'none',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type={mode === 'existing' ? 'button' : 'submit'}
                  variant="contained"
                  color="primary"
                  sx={{
                    flex: 1,
                    borderRadius: 3,
                    fontSize: 16,
                    textTransform: 'none',
                  }}
                  disabled={mode === 'existing' && !selectedPatient}
                  onClick={() => {
                    if (mode === 'existing' && selectedPatient) {
                      navigate(`/admin/billing/pharmacy/new/${selectedPatient.value}`);
                      onClose();
                    }
                  }}
                >
                  New Invoice
                </Button>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default NewPatientPopUp;

